"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHotelRoomAvailability = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const dayjs = require("dayjs");
const slugify_1 = require("../slugify");
const _ = require("lodash");
/**
 * Filters contracts, products, and rates based on book_before date
 * @param contracts - Array of HotelContract objects
 * @returns Filtered array of HotelContract objects
 */
function filterContractsByBookBeforeDate(contracts) {
    const currentDate = new Date();
    // Filter each contract based on the book_before date criteria
    const filteredContracts = contracts
        .map((contract) => {
        // For each contract, filter products
        const filteredProducts = contract.products
            ?.map((product) => {
            // For each product, filter rates based on book_before date
            const validRates = product.rates?.filter((rate) => {
                // If book_before is set, check if it’s a future date
                return (!rate.book_before || new Date(rate.book_before) >= currentDate);
            }) || [];
            // Return the product with valid rates if rates are not empty
            return validRates.length > 0
                ? { ...product, rates: validRates }
                : null;
        })
            .filter(Boolean); // Remove null values (products with no rates)
        // Return the contract with valid products if products are not empty
        return filteredProducts.length > 0
            ? { ...contract, products: filteredProducts }
            : null;
    })
        .filter(Boolean); // Remove null values (contracts with no products)
    // Return the filtered array of contracts
    return filteredContracts;
}
const getHotelRoomAvailability = (dateRange, contracts, contract_type) => {
    const dayOfWeekMap = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    contracts = filterContractsByBookBeforeDate(contracts);
    const isInDateRangeAndAvailableOnDay = (startDate, endDate, checkDate, availabilityDays) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const check = new Date(checkDate);
        const dayOfWeek = dayOfWeekMap[check.getDay()];
        return check >= start && check <= end && availabilityDays[dayOfWeek];
    };
    const getPriceBandForDate = (priceBands, date) => {
        const band = priceBands.find((band) => {
            const start = new Date(band.from_date);
            const end = new Date(band.to_date);
            const check = new Date(date);
            return check >= start && check <= end;
        });
        return band;
    };
    const getContractWarningsForDate = (contract, date) => {
        const warnings = [];
        if (contract.contract_warnings) {
            contract.contract_warnings.forEach((warning) => {
                const start = new Date(warning.from_date);
                const end = new Date(warning.to_date);
                const check = new Date(date);
                if (check >= start && check <= end) {
                    warnings.push(warning.message);
                }
            });
        }
        return warnings;
    };
    const getContractGalaDinnersForDate = (contract, date) => {
        const dinners = [];
        if (contract?.contract_dinners) {
            contract.contract_dinners.forEach((dinner) => {
                const dinnerDate = new Date(dinner.date);
                const check = new Date(date);
                const isSameDay = (d1, d2) => {
                    return (d1.getFullYear() === d2.getFullYear() &&
                        d1.getMonth() === d2.getMonth() &&
                        d1.getDate() === d2.getDate());
                };
                if (isSameDay(dinnerDate, check)) {
                    dinners.push(dinner);
                }
            });
        }
        return dinners;
    };
    const availabilityBySupplier = {};
    if (contract_type)
        contracts = contracts.filter((contract) => contract.type === contract_type);
    contracts.forEach((contract) => {
        const { supplier_id, supplier, products, price_bands } = contract;
        if (!availabilityBySupplier[supplier_id]) {
            availabilityBySupplier[supplier_id] = {
                id: supplier_id,
                name: supplier.name,
                currency: supplier.currency,
                rooms: {},
            };
        }
        if (!products)
            return console.error("No products found in contract", contract);
        products.forEach((product) => {
            const { hotel_room, board_options, occupancy, availability_days } = product;
            const roomName = hotel_room?.name.en;
            const room = hotel_room;
            const roomId = hotel_room?.id || "";
            let maxOccupancy = hotel_room?.max_occupancy;
            const possibleOccupancies = occupancy === 0
                ? [...Array(maxOccupancy).keys()].map((x) => x + 1)
                : [occupancy];
            if (occupancy !== 0) {
                maxOccupancy = occupancy;
            }
            board_options.forEach((option) => {
                if (!availabilityBySupplier[supplier_id].rooms[roomId]) {
                    availabilityBySupplier[supplier_id].rooms[roomId] = {
                        room_type: roomName,
                        room,
                        maxOccupancy,
                        boardOptions: {},
                    };
                }
                possibleOccupancies.forEach((occupancyOption) => {
                    if (!availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type]) {
                        availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type] = {};
                    }
                    if (!availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][`${occupancyOption}_pax`]) {
                        availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][`${occupancyOption}_pax`] = {
                            dates: {},
                            extraBedAvailable: false,
                        };
                    }
                    dateRange.forEach((date) => {
                        if (isInDateRangeAndAvailableOnDay(contract.start_date, contract.end_date, date, availability_days)) {
                            const isStopSell = contract.blackout_dates?.some((blackout) => blackout.date === date && blackout.hotel_room_id === roomId);
                            const isBlackOut = contract.contract_blackout_dates?.some((blackout) => blackout.from_date <= date && blackout.to_date >= date);
                            // If this contract is stop-sold for this room/date, skip adding it entirely
                            if (isStopSell) {
                                return;
                            }
                            if (!price_bands)
                                return console.error("No price bands found in contract", contract);
                            const priceBand = getPriceBandForDate(price_bands, date);
                            const warnings = getContractWarningsForDate(contract, date);
                            const galaDinners = getContractGalaDinnersForDate(contract, date);
                            const extraBedCost = option.extra_bed_cost;
                            const extraBedAvailable = option.extra_bed_available;
                            const surchargePerPerson = option.surcharge_per_person;
                            const room_price_bands = [
                                ...(availabilityBySupplier[supplier_id].rooms[roomId]
                                    .boardOptions[option.type][`${occupancyOption}_pax`].dates[date]?.priceBandIds || []),
                                ...(priceBand ? [priceBand.id] : []),
                            ];
                            // Only mark the date as stop-sell if *every* matching contract is stop-sold
                            const allContractsStopSold = contracts
                                .filter((c) => c.supplier_id === supplier_id &&
                                new Date(c.start_date) <= new Date(date) &&
                                new Date(c.end_date) >= new Date(date))
                                .every((c) => c.blackout_dates?.some((b) => b.date === date && b.hotel_room_id === roomId));
                            availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][`${occupancyOption}_pax`].dates[date] = {
                                surchargePerPerson,
                                extraBedCost,
                                extraBedAvailable,
                                paymentTermsDays: priceBand?.payment_terms_days,
                                priceBandIds: room_price_bands.map((band) => band),
                                isStopSell: allContractsStopSold,
                                isBlackOut,
                                warnings,
                                dinners: galaDinners,
                            };
                            // Add rate for extra bed if the current occupancy is at max
                            if (extraBedAvailable && occupancyOption === maxOccupancy) {
                                const newOccupancyOption = occupancyOption + 1;
                                if (!availabilityBySupplier[supplier_id].rooms[roomId]
                                    .boardOptions[option.type][`${newOccupancyOption}_pax_extra_bed`]) {
                                    availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][`${newOccupancyOption}_pax_extra_bed`] = {
                                        dates: {},
                                        warnings,
                                        dinners: galaDinners,
                                        priceBandIds: room_price_bands,
                                        surchargePerPerson,
                                        extraBedCost,
                                        paymentTermsDays: priceBand?.payment_terms_days,
                                        isStopSell: allContractsStopSold,
                                        isBlackOut,
                                        extraBedAvailable: true,
                                    };
                                }
                                availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][`${newOccupancyOption}_pax_extra_bed`].dates[date] = {
                                    surchargePerPerson,
                                    extraBedCost,
                                    extraBedAvailable,
                                    priceBandIds: room_price_bands,
                                    isStopSell: allContractsStopSold,
                                    paymentTermsDays: priceBand?.payment_terms_days,
                                    warnings,
                                    dinners: galaDinners,
                                    isBlackOut,
                                    extraBed: true,
                                };
                            }
                        }
                    });
                });
            });
        });
    });
    const resultArray = Object.values(availabilityBySupplier).map((supplier) => {
        const availability = Object.entries(supplier.rooms)
            .map(([roomId, { room, maxOccupancy, boardOptions }]) => {
            if (!room)
                return null;
            // console.log('boardOptions', boardOptions)
            const boardOptionsArray = Object.entries(boardOptions).flatMap(([optionType, occupancies]) => {
                return Object.entries(occupancies)
                    .map(([occupancy, { dates, extraBedAvailable }]) => {
                    const rate_ref = `rate_${(0, slugify_1.slugify)(room.name.en)}_${(0, slugify_1.slugify)(supplier.name)}_${(0, slugify_1.slugify)(optionType)}_${parseInt(occupancy)}_pax${extraBedAvailable ? "_extra_bed" : ""}`;
                    return {
                        type: optionType,
                        rate_ref: rate_ref,
                        occupancy: parseInt(occupancy),
                        extra_bed: extraBedAvailable || false,
                        dates: Object.entries(dates).map(([date, { surchargePerPerson, extraBedCost, extraBedAvailable, isStopSell, isBlackOut, priceBandIds, paymentTermsDays, warnings, dinners, extraBed, },]) => {
                            const matchingContract = contracts.find((c) => c.supplier_id === supplier.id &&
                                new Date(c.start_date) <= new Date(date) &&
                                new Date(c.end_date) >= new Date(date));
                            const rates = contracts
                                .filter((contract) => contract.supplier_id === supplier.id &&
                                contract.start_date <= date &&
                                contract.end_date >= date)
                                .flatMap((contract) => {
                                const contractIsStopSell = contract.blackout_dates?.some((b) => b.date === date && b.hotel_room_id === roomId) ?? false;
                                if (contractIsStopSell)
                                    return [];
                                if (!contract.products)
                                    return [];
                                const products = contract.products
                                    .filter((product) => {
                                    const key = dayOfWeekMap[new Date(date).getDay()];
                                    let filter_occupancy = false;
                                    const is_extra_bed_occupancy = String(occupancy).includes("_extra_bed");
                                    // if it is any, we want to include it
                                    if (product.occupancy === 0) {
                                        filter_occupancy = true;
                                    }
                                    else {
                                        filter_occupancy =
                                            product.occupancy ===
                                                parseInt(occupancy) +
                                                    (is_extra_bed_occupancy ? -1 : 0);
                                    }
                                    return (product?.hotel_room?.id === roomId &&
                                        product.availability_days[key] &&
                                        filter_occupancy);
                                })
                                    .flatMap((product) => {
                                    if (!product.rates)
                                        return [];
                                    return product.rates.filter((rate) => {
                                        if (!priceBandIds.includes(rate.price_band_id))
                                            return false;
                                        if (rate.min_nights > dateRange.length)
                                            return false;
                                        return true;
                                    });
                                });
                                return products.map((product) => {
                                    return {
                                        ...product,
                                        contract_type: contract.type,
                                    };
                                });
                            })
                                .sort((a, b) => {
                                const daysInAdvance = dayjs(date).diff(dayjs(), "day");
                                const nights = dateRange.length;
                                const effective = (r) => {
                                    // fallback if sell_price missing
                                    const base = (typeof r.sell_price === "number"
                                        ? r.sell_price
                                        : 0) || (r.cost_price || 0) + (r.margin || 0);
                                    const d = r.discount; // may be undefined
                                    if (!d)
                                        return base;
                                    // early-bird applicable % at this lead time
                                    const eb = Array.isArray(d.early_bird_discounts)
                                        ? d.early_bird_discounts
                                            .filter((x) => daysInAdvance >=
                                            (x.min_days_in_advance || 0))
                                            .reduce((max, x) => Math.max(max, Number(x.discount_percentage || 0)), 0)
                                        : 0;
                                    // long-stay applicable % for this length of stay (if present)
                                    const ls = Array.isArray(d.long_stay_discounts)
                                        ? d.long_stay_discounts
                                            .filter((x) => nights >= (x.min_nights || 0))
                                            .reduce((max, x) => Math.max(max, Number(x.discount_percentage || 0)), 0)
                                        : 0;
                                    const pct = d.stackable
                                        ? eb + ls
                                        : Math.max(eb, ls);
                                    return pct > 0 ? base - (base * pct) / 100 : base;
                                };
                                return effective(a) - effective(b);
                            });
                            return {
                                date,
                                board_surcharge: surchargePerPerson,
                                extra_bed_cost: extraBedCost,
                                warnings,
                                dinners,
                                extra_bed_available: extraBedAvailable,
                                payment_terms_days: paymentTermsDays,
                                is_stop_sell: isStopSell,
                                is_blackout: isBlackOut,
                                extraBed: extraBed || false,
                                rate_code: matchingContract?.rate_code || "",
                                ...(rates.length > 0 ? rates[0] : null),
                            };
                        }),
                    };
                })
                    .filter((boardOption) => boardOption.dates.length === dateRange.length);
            });
            return {
                hotel_room_id: roomId,
                room_ref: `rate_${(0, slugify_1.slugify)(room.name.en)}_${(0, slugify_1.slugify)(supplier.name)}`,
                room,
                max_occupancy: maxOccupancy,
                board_options: boardOptionsArray,
            };
        })
            .filter((room) => room && room?.board_options?.length > 0)
            .filter((room) => {
            return (room &&
                room.board_options.every((option) => {
                    return option.dates.every((date) => {
                        if (date.book_before &&
                            dayjs(date.book_before).toDate() < new Date()) {
                            return false;
                        }
                        return date;
                    });
                }));
        })
            .sort((a, b) => {
            // sorty by cheapest rate
            const aRate = (a &&
                a.board_options[0].dates.reduce((prev, current) => {
                    if (prev.sell_price && current.sell_price)
                        return prev.sell_price < current.sell_price ? prev : current;
                    return prev;
                }).sell_price) ||
                0;
            const bRate = (b &&
                b.board_options[0].dates.reduce((prev, current) => {
                    if (prev.sell_price && current.sell_price)
                        return prev.sell_price < current.sell_price ? prev : current;
                    return prev;
                }).sell_price) ||
                0;
            return aRate - bRate;
        })
            .map((room) => {
            if (!room)
                return null;
            // Group board_options by rate_ref and select the cheapest total price for each group
            const board_options = room.board_options;
            const grouped_board_options = _.groupBy(board_options, "rate_ref");
            const new_board_options = [];
            Object.values(grouped_board_options).forEach((group) => {
                const cheapest_option = group.reduce((prev, current) => {
                    const prev_total = prev.dates.reduce((sum, date) => sum + date.sell_price, 0);
                    const current_total = current.dates.reduce((sum, date) => sum + date.sell_price, 0);
                    return prev_total < current_total ? prev : current;
                });
                new_board_options.push(cheapest_option);
            });
            return {
                ..._.cloneDeep(room),
                board_options: new_board_options,
            };
        });
        return {
            id: supplier.id,
            name: supplier.name,
            currency: supplier.currency,
            dates: dateRange,
            availability,
        };
    });
    return resultArray;
};
exports.getHotelRoomAvailability = getHotelRoomAvailability;
